import { useState } from "react";
import { Button, Checkbox, Dropdown, Form, Header, Icon, Modal, Segment } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import ErrorsView from "./ErrorsView";
import PermitFieldsGrid from "./PermitFieldsGrid";
import FeatureConfigurationGrid from "./FeatureConfigurationGrid";
import {
  ConfigurationMultipleChoice,
  GisBoundaryFeature,
  AsBuiltFeature,
  GangMembersFeature,
  SitePhotoFeature,
  AffectedServicesFeature,
  PermitConfiguration,
} from "../models/PermitFeatures";
import { StateKeyList } from "../models/StateKey";

export type {
  ConfigurationMultipleChoice,
  GisBoundaryFeature,
  AsBuiltFeature,
  GangMembersFeature,
  SitePhotoFeature,
  AffectedServicesFeature,
  PermitConfiguration,
} from "../models/PermitFeatures";

export interface TemplateField {
  key: string;
  label: string;
  fieldType: string;
  // Text
  maxLength?: number;
  isMultiLine?: boolean;
  // Boolean
  defaultValue?: boolean | null;
  trueLabel?: string;
  falseLabel?: string;
  // Number
  minValue?: number;
  maxValue?: number;
  decimalPlaces?: number;
  unit?: string;
  // Date
  includeTime?: boolean;
  // Stage config
  requiredForState?: string | null;
  editableAtStages?: string[];
}

export interface PermitTemplateFormData {
  id?: string;
  name: string;
  fields: TemplateField[];
  catAndGennyEnabled: boolean;
  gisBoundary: GisBoundaryFeature;
  asBuilt: AsBuiltFeature;
  gangMembers: GangMembersFeature;
  sitePhoto: SitePhotoFeature;
  affectedServices: AffectedServicesFeature;
  configuration: PermitConfiguration;
}

const fieldTypeLabels: Record<string, string> = {
  Text: "Text",
  Number: "Number",
  Boolean: "Boolean",
  Date: "Date",
};

const requiredForStateOptions = [
  { key: "none", value: "", text: "(Optional)" },
  ...[StateKeyList.REQUESTED, StateKeyList.READY_TO_ISSUE, StateKeyList.ISSUED].map((s) => ({
    key: s.key,
    value: s.key,
    text: s.name,
  })),
];

const editableStageOptions = [
  StateKeyList.REQUEST_IN_PROGRESS,
  StateKeyList.REJECTED,
  StateKeyList.IN_PROGRESS,
  StateKeyList.READY_TO_ISSUE,
].map((s) => ({ key: s.key, value: s.key, text: s.name }));

const defaultValueOptions = [
  { key: "notset", text: "Not Set", value: "notset" },
  { key: "true", text: "True", value: "true" },
  { key: "false", text: "False", value: "false" },
];

const addFieldOptions = [
  { key: "Text", text: "Text Field", value: "Text", icon: "font" },
  { key: "Number", text: "Number Field", value: "Number", icon: "hashtag" },
  { key: "Boolean", text: "Boolean Field", value: "Boolean", icon: "toggle on" },
  { key: "Date", text: "Date Field", value: "Date", icon: "calendar" },
];

interface Props {
  formData: PermitTemplateFormData;
  formErrors: FormError;
  errors?: string[];
  onChange: (formData: PermitTemplateFormData) => void;
}

/** Generates a stable key from a label */
const generateKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

// ─── Feature Settings Modal ────────────────────────────────────────────────────

const FeatureSettingsModal = ({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <Modal open={open} onClose={onClose} size="small">
    <Modal.Header>{title} Settings</Modal.Header>
    <Modal.Content>
      <Form>{children}</Form>
    </Modal.Content>
    <Modal.Actions>
      <Button primary onClick={onClose}>
        Done
      </Button>
    </Modal.Actions>
  </Modal>
);

// ─── Edit Field Modal ──────────────────────────────────────────────────────────

const EditFieldModal = ({
  open,
  field,
  fieldIndex,
  formErrors,
  onSave,
  onClose,
}: {
  open: boolean;
  field: TemplateField;
  fieldIndex: number;
  formErrors: FormError;
  onSave: (updated: TemplateField) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState<TemplateField>(field);

  const handleChange = (updates: Partial<TemplateField>) => {
    setDraft({ ...draft, ...updates });
  };

  // Reset draft when the modal opens with a new field
  useState(() => {
    setDraft(field);
  });

  const renderTypeSpecificFields = () => {
    switch (draft.fieldType) {
      case "Text":
        return (
          <Form.Group widths="equal">
            <Form.Field>
              <label>Max Length</label>
              <Form.Input
                type="number"
                value={draft.maxLength ?? ""}
                onChange={(_, { value }) => handleChange({ maxLength: value ? parseInt(value) : undefined })}
                placeholder="No limit"
              />
            </Form.Field>
            <Form.Field>
              <label>Multi-line</label>
              <Checkbox
                toggle
                checked={draft.isMultiLine ?? false}
                onChange={(_, { checked }) => handleChange({ isMultiLine: !!checked })}
              />
            </Form.Field>
          </Form.Group>
        );
      case "Number":
        return (
          <>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Min Value</label>
                <Form.Input
                  type="number"
                  value={draft.minValue ?? ""}
                  onChange={(_, { value }) => handleChange({ minValue: value ? parseFloat(value) : undefined })}
                  placeholder="No minimum"
                />
              </Form.Field>
              <Form.Field>
                <label>Max Value</label>
                <Form.Input
                  type="number"
                  value={draft.maxValue ?? ""}
                  onChange={(_, { value }) => handleChange({ maxValue: value ? parseFloat(value) : undefined })}
                  placeholder="No maximum"
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Decimal Places</label>
                <Form.Input
                  type="number"
                  value={draft.decimalPlaces ?? ""}
                  onChange={(_, { value }) => handleChange({ decimalPlaces: value ? parseInt(value) : undefined })}
                  placeholder="e.g. 2"
                />
              </Form.Field>
              <Form.Field>
                <label>Unit</label>
                <Form.Input
                  value={draft.unit ?? ""}
                  onChange={(_, { value }) => handleChange({ unit: value })}
                  placeholder="e.g. metres"
                />
              </Form.Field>
            </Form.Group>
          </>
        );
      case "Boolean":
        return (
          <>
            <Form.Group widths="equal">
              <Form.Field>
                <label>True Label</label>
                <Form.Input
                  value={draft.trueLabel ?? ""}
                  onChange={(_, { value }) => handleChange({ trueLabel: value })}
                  placeholder="e.g. Yes"
                />
              </Form.Field>
              <Form.Field>
                <label>False Label</label>
                <Form.Input
                  value={draft.falseLabel ?? ""}
                  onChange={(_, { value }) => handleChange({ falseLabel: value })}
                  placeholder="e.g. No"
                />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field>
                <label>Default Value</label>
                <Dropdown
                  selection
                  options={defaultValueOptions}
                  value={draft.defaultValue === true ? "true" : draft.defaultValue === false ? "false" : "notset"}
                  onChange={(_, { value }) =>
                    handleChange({ defaultValue: value === "true" ? true : value === "false" ? false : null })
                  }
                />
              </Form.Field>
            </Form.Group>
          </>
        );
      case "Date":
        return (
          <Form.Group>
            <Form.Field>
              <Checkbox
                toggle
                label="Include Time"
                checked={draft.includeTime ?? false}
                onChange={(_, { checked }) => handleChange({ includeTime: !!checked })}
              />
            </Form.Field>
          </Form.Group>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Edit {fieldTypeLabels[draft.fieldType] ?? draft.fieldType} Field</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths="equal">
            <Form.Field required>
              <label>Label</label>
              <Form.Input
                value={draft.label ?? ""}
                onChange={(_, { value }) => handleChange({ label: value, key: generateKey(value) })}
                placeholder="e.g. Maximum Depth"
                error={formErrors[`field_${fieldIndex}_label`]}
              />
            </Form.Field>
          </Form.Group>

          <Header as="h5" dividing>
            Type-specific Settings
          </Header>
          {renderTypeSpecificFields()}

          <Header as="h5" dividing>
            Stage Configuration
          </Header>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Required for State</label>
              <Form.Select
                options={requiredForStateOptions}
                value={draft.requiredForState ?? ""}
                onChange={(_, { value }) => handleChange({ requiredForState: value ? (value as string) : null })}
                placeholder="Optional"
              />
            </Form.Field>
            <Form.Field>
              <label>Editable at Stages</label>
              <Form.Select
                multiple
                options={editableStageOptions}
                value={draft.editableAtStages ?? []}
                onChange={(_, { value }) => handleChange({ editableAtStages: value as string[] })}
                placeholder="Select stages"
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary onClick={() => onSave(draft)}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

// ─── Main Form Component ───────────────────────────────────────────────────────

const EditPermitTemplateForm = ({ formData, formErrors, errors, onChange }: Props) => {
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [featureModal, setFeatureModal] = useState<string | null>(null);

  const handleChange = (e: any, { name, value }: any) => {
    onChange({ ...formData, [name]: value });
  };

  const handleAddField = (fieldType: string) => {
    const newField: TemplateField = {
      key: "",
      label: "",
      fieldType,
      editableAtStages: [],
      requiredForState: null,
    };
    onChange({ ...formData, fields: [...formData.fields, newField] });
    setEditingFieldIndex(formData.fields.length);
  };

  const handleRemoveField = (index: number) => {
    const fields = formData.fields.filter((_, i) => i !== index);
    onChange({ ...formData, fields });
  };

  const handleMoveField = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;
    const fields = [...formData.fields];
    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
    onChange({ ...formData, fields });
  };

  const handleFieldSave = (updated: TemplateField) => {
    if (editingFieldIndex !== null) {
      const fields = [...formData.fields];
      fields[editingFieldIndex] = updated;
      onChange({ ...formData, fields });
    }
    setEditingFieldIndex(null);
  };

  return (
    <Form>
      <Form.Group widths="equal">
        <Form.Field required>
          <label>Name</label>
          <Form.Input
            error={formErrors.name}
            name="name"
            value={formData.name ?? ""}
            onChange={handleChange}
            placeholder="e.g. Permit to Dig"
          />
        </Form.Field>
      </Form.Group>

      <Header as="h5">Features</Header>
      <Segment>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1em", alignItems: "center" }}>
          {/* Cat & Genny */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="Cat &amp; Genny"
              checked={formData.catAndGennyEnabled}
              onChange={(_, { checked }) => onChange({ ...formData, catAndGennyEnabled: !!checked })}
            />
          </div>

          {/* GIS Boundary */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="GIS Boundary"
              checked={formData.gisBoundary.enabled}
              onChange={(_, { checked }) =>
                onChange({ ...formData, gisBoundary: { ...formData.gisBoundary, enabled: !!checked } })
              }
            />
            <Button
              icon="cog"
              size="mini"
              basic
              onClick={() => setFeatureModal("gisBoundary")}
              title="Settings"
              disabled={!formData.gisBoundary.enabled}
            />
          </div>

          {/* As-Built */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="As-Built"
              checked={formData.asBuilt.enabled}
              onChange={(_, { checked }) =>
                onChange({ ...formData, asBuilt: { ...formData.asBuilt, enabled: !!checked } })
              }
            />
            <Button
              icon="cog"
              size="mini"
              basic
              onClick={() => setFeatureModal("asBuilt")}
              title="Settings"
              disabled={!formData.asBuilt.enabled}
            />
          </div>

          {/* Gang Members */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="Gang Members"
              checked={formData.gangMembers.enabled}
              onChange={(_, { checked }) =>
                onChange({ ...formData, gangMembers: { ...formData.gangMembers, enabled: !!checked } })
              }
            />
            <Button
              icon="cog"
              size="mini"
              basic
              onClick={() => setFeatureModal("gangMembers")}
              title="Settings"
              disabled={!formData.gangMembers.enabled}
            />
          </div>

          {/* Site Photo */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="Site Photo"
              checked={formData.sitePhoto.enabled}
              onChange={(_, { checked }) =>
                onChange({ ...formData, sitePhoto: { ...formData.sitePhoto, enabled: !!checked } })
              }
            />
            <Button
              icon="cog"
              size="mini"
              basic
              onClick={() => setFeatureModal("sitePhoto")}
              title="Settings"
              disabled={!formData.sitePhoto.enabled}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <Checkbox
              toggle
              label="Affected Services"
              checked={formData.affectedServices.enabled}
              onChange={(_, { checked }) =>
                onChange({ ...formData, affectedServices: { ...formData.affectedServices, enabled: !!checked } })
              }
            />
          </div>
        </div>
      </Segment>

      {/* Configuration */}
      <Header as="h5">Configuration</Header>
      <Segment>
        <Button
          icon="cog"
          size="small"
          basic
          labelPosition="left"
          content="Edit Configuration"
          onClick={() => setFeatureModal("configuration")}
        />
      </Segment>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.5em" }}>
        <Header as="h5" style={{ flex: 1, marginBottom: 0 }}>
          Template Fields ({formData.fields.length})
        </Header>
        <Dropdown text="Add Field" icon="plus" floating labeled button className="icon tiny green basic">
          <Dropdown.Menu>
            {addFieldOptions.map((opt) => (
              <Dropdown.Item key={opt.key} icon={opt.icon} text={opt.text} onClick={() => handleAddField(opt.value)} />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {formData.fields.length === 0 && (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="file outline" />
            No fields defined
          </Header>
          <p>Click &quot;Add Field&quot; to add template fields to this blueprint.</p>
        </Segment>
      )}

      {formData.fields.length > 0 && (
        <PermitFieldsGrid
          fields={formData.fields}
          onEditField={(index) => setEditingFieldIndex(index)}
          onMoveUp={(index) => handleMoveField(index, -1)}
          onMoveDown={(index) => handleMoveField(index, 1)}
          onRemoveField={handleRemoveField}
        />
      )}

      <ErrorsView errors={errors} />

      {editingFieldIndex !== null && formData.fields[editingFieldIndex] && (
        <EditFieldModal
          open={true}
          field={formData.fields[editingFieldIndex]}
          fieldIndex={editingFieldIndex}
          formErrors={formErrors}
          onSave={handleFieldSave}
          onClose={() => setEditingFieldIndex(null)}
        />
      )}

      {/* GIS Boundary Settings Modal */}
      <FeatureSettingsModal
        open={featureModal === "gisBoundary"}
        title="GIS Boundary"
        onClose={() => setFeatureModal(null)}
      >
        <FeatureConfigurationGrid
          feature={formData.gisBoundary}
          onChange={(updated) => onChange({ ...formData, gisBoundary: updated as GisBoundaryFeature })}
        />
      </FeatureSettingsModal>

      {/* As-Built Settings Modal */}
      <FeatureSettingsModal open={featureModal === "asBuilt"} title="As-Built" onClose={() => setFeatureModal(null)}>
        <FeatureConfigurationGrid
          feature={formData.asBuilt}
          onChange={(updated) => onChange({ ...formData, asBuilt: updated as AsBuiltFeature })}
        />
      </FeatureSettingsModal>

      {/* Gang Members Settings Modal */}
      <FeatureSettingsModal
        open={featureModal === "gangMembers"}
        title="Gang Members"
        onClose={() => setFeatureModal(null)}
      >
        <FeatureConfigurationGrid
          feature={formData.gangMembers}
          onChange={(updated) => onChange({ ...formData, gangMembers: updated as GangMembersFeature })}
        />
      </FeatureSettingsModal>

      {/* Site Photo Settings Modal */}
      <FeatureSettingsModal
        open={featureModal === "sitePhoto"}
        title="Site Photo"
        onClose={() => setFeatureModal(null)}
      >
        <FeatureConfigurationGrid
          feature={formData.sitePhoto}
          onChange={(updated) => onChange({ ...formData, sitePhoto: updated as SitePhotoFeature })}
        />
      </FeatureSettingsModal>

      {/* Configuration Settings Modal */}
      <FeatureSettingsModal
        open={featureModal === "configuration"}
        title="Configuration"
        onClose={() => setFeatureModal(null)}
      >
        <FeatureConfigurationGrid
          feature={formData.configuration}
          onChange={(updated) => onChange({ ...formData, configuration: updated as PermitConfiguration })}
          excludeKeys={[]}
          nullableValueTypes={{ maxPermitDurationDays: "number", maxPermitValidInAdvanceDays: "number" }}
        />
      </FeatureSettingsModal>
    </Form>
  );
};

export default EditPermitTemplateForm;
